import { useEffect, useState } from "react";
import FireworksCanvas from "./components/FireworksCanvas";

export default function Home() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="relative w-full min-h-screen overflow-hidden bg-[#040c2e]">
      {/* Deep night sky gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 700px at 50% 110%, #0b2a6b 0%, #061a52 40%, #030a2b 75%, #01061e 100%)",
        }}
      />

      {/* Subtle star field */}
      <div className="pointer-events-none absolute inset-0">
        {Array.from({ length: 60 }).map((_, i) => {
          const top = (i * 53) % 100;
          const left = (i * 97) % 100;
          const size = ((i * 7) % 3) + 1;
          const delay = (i % 8) * 0.4;
          return (
            <span
              key={i}
              className="absolute rounded-full bg-white/70 animate-twinkle"
              style={{
                top: `${top}%`,
                left: `${left}%`,
                width: `${size}px`,
                height: `${size}px`,
                animationDelay: `${delay}s`,
                opacity: 0.5,
              }}
            />
          );
        })}
      </div>

      {/* Fireworks canvas */}
      <FireworksCanvas />

      {/* Decorative concentric circle rings — center of the composition */}
      <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
        <div className="relative" style={{ width: "min(1200px, 95vw)", aspectRatio: "1 / 1" }}>
          <div className="absolute inset-0 rounded-full border border-white/15" />
          <div className="absolute inset-[8%] rounded-full border border-white/12" />
          <div className="absolute inset-[18%] rounded-full border border-white/10" />
          {/* left sparkle */}
          <i
            className="ri-sparkling-fill absolute text-white/70"
            style={{ left: "6%", top: "48%", fontSize: "22px" }}
          />
          {/* right sparkle */}
          <i
            className="ri-sparkling-fill absolute text-white/70"
            style={{ right: "6%", top: "48%", fontSize: "22px" }}
          />
        </div>
      </div>

      {/* Top row: logo | pill nav | icon */}
      <div
        className={`absolute left-0 right-0 top-6 md:top-8 z-50 w-full px-6 md:px-10 flex items-center justify-between transition-all duration-1000 ease-out ${
          mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
        }`}
      >
        {/* Logo */}
        <a
          href="#"
          className="text-white text-2xl tracking-tight cursor-pointer"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, letterSpacing: "-0.02em" }}
        >
          LU
        </a>

        {/* Center pill nav */}
        <nav
          className="hidden md:flex items-center gap-14 border border-white/25 rounded-full px-10 py-3 backdrop-blur-sm"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <a href="#" className="text-white text-xs tracking-[0.3em] uppercase hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap">Home</a>
          <a href="#" className="text-white text-xs tracking-[0.3em] uppercase hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap">Services</a>
          <a href="#" className="text-white text-xs tracking-[0.3em] uppercase hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap">About Us</a>
          <a href="#" className="text-white text-xs tracking-[0.3em] uppercase hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap">Contacts</a>
        </nav>

        {/* Right circular icon */}
        <a
          href="#"
          aria-label="Instagram"
          className="w-11 h-11 flex items-center justify-center rounded-full border border-white/30 text-white hover:bg-white/10 transition-colors cursor-pointer"
        >
          <i className="ri-instagram-line text-lg" />
        </a>

        {/* Mobile menu */}
        <button className="md:hidden text-white text-2xl w-9 h-9 flex items-center justify-center cursor-pointer" aria-label="menu">
          <i className="ri-menu-line" />
        </button>
      </div>

      {/* Left side paragraph */}
      <div
        className={`absolute left-6 md:left-14 top-1/2 -translate-y-1/2 z-30 max-w-[360px] transition-all duration-1000 delay-200 ease-out ${
          mounted ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"
        }`}
      >
        <p
          className="text-white text-base md:text-lg leading-snug"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          A dazzling sky show made of light and color. We craft moments where every spark becomes a memory beneath the stars.
        </p>
      </div>

      {/* Right side CTA button */}
      <div
        className={`absolute right-6 md:right-14 top-1/2 -translate-y-1/2 z-30 transition-all duration-1000 delay-200 ease-out ${
          mounted ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"
        }`}
      >
        <a
          href="#"
          className="inline-flex items-center justify-center bg-white text-[#0a1440] rounded-full px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}
        >
          To Visit
        </a>
      </div>

      {/* Giant overflowing wordmark at bottom */}
      <h1
        className={`pointer-events-none absolute left-0 right-0 bottom-[-2vw] z-40 w-full text-center text-white whitespace-nowrap transition-all duration-1000 delay-300 ease-out ${
          mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 300,
          fontSize: "clamp(180px, 28vw, 460px)",
          lineHeight: 0.85,
          letterSpacing: "-0.04em",
        }}
      >
        lumina
      </h1>
    </div>
  );
}